/**
 * runidea.ai — Phase 13 + Phase 44 Batch BF2 — Flip Financials tab.
 *
 * Specialised Financials view for `deal_type === "flip"`. Renders the
 * compute_flip(...) KPI shape: ARV, total project cost, gross profit,
 * annualised ROI, 70%-rule check.
 *
 * Phase 44 Batch BF2:
 *   - AVM Discrepancy warning banner (asking >40% below AVM).
 *   - ARV source pill (Verified / Estimated / Unverified).
 *   - "Exit as Rental" collapsible: lets the analyst compare the
 *      flip thesis to a buy-and-hold thesis on the same property.
 */
import { useState } from "react";
import { useFmt } from "@/lib/useFmt";
import {
  Hammer, TrendingUp, AlertCircle, CheckCircle2, AlertTriangle,
  ChevronDown, ChevronRight, ShieldCheck, ShieldAlert, Shield,
} from "lucide-react";

function Row({ label, value, indent = false, bold = false, divider = false, tone = "neutral" }) {
  const cls = {
    neutral: "text-slate-900",
    good: "text-emerald-700",
    bad: "text-red-600",
    muted: "text-slate-500",
  }[tone];
  return (
    <tr className={divider ? "border-t border-slate-200" : ""}>
      <td className={`py-2.5 pr-4 text-sm ${indent ? "pl-8 text-slate-500" : "pl-0 text-slate-700"} ${bold ? "font-semibold text-slate-900" : ""}`}>
        {label}
      </td>
      <td className={`py-2.5 pl-4 text-right text-sm tabular-nums ${bold ? "font-bold" : ""} ${cls}`}>
        {value}
      </td>
    </tr>
  );
}


function ArvSourcePill({ source }) {
  const v = String(source || "unverified").toLowerCase();
  const conf = {
    verified: {
      label: "ARV Verified",
      bg: "bg-emerald-50",
      ring: "ring-emerald-200",
      text: "text-emerald-800",
      Icon: ShieldCheck,
    },
    estimated: {
      label: "ARV Estimated",
      bg: "bg-amber-50",
      ring: "ring-amber-200",
      text: "text-amber-800",
      Icon: Shield,
    },
    unverified: {
      label: "ARV Unverified",
      bg: "bg-rose-50",
      ring: "ring-rose-200",
      text: "text-rose-800",
      Icon: ShieldAlert,
    },
  }[v] || {
    label: "ARV Unverified",
    bg: "bg-rose-50",
    ring: "ring-rose-200",
    text: "text-rose-800",
    Icon: ShieldAlert,
  };
  const Icon = conf.Icon;
  return (
    <span
      data-testid="flip-arv-source-pill"
      data-arv-source={v}
      className={`inline-flex items-center gap-1 rounded-full ${conf.bg} px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 ${conf.ring} ${conf.text}`}
    >
      <Icon className="h-3 w-3" /> {conf.label}
    </span>
  );
}


function AvmDiscrepancyBanner({ deal, fmt }) {
  if (!deal?.avm_discrepancy_warning) return null;
  const pctBelow = Number(deal.asking_vs_avm_pct_below || 0) * 100;
  return (
    <div
      data-testid="flip-avm-discrepancy-banner"
      className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 shadow-sm"
      role="alert"
    >
      <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-700" />
      <div className="min-w-0 text-sm">
        <p className="font-semibold text-amber-900">
          Asking price {pctBelow.toFixed(0)}% below AVM —&nbsp;verify the listing
        </p>
        <p className="mt-1 text-amber-800">
          Asking <span className="font-medium tabular-nums">{fmt.money(deal.asking_price_display ?? deal.original_purchase_price ?? deal.purchase_price)}</span>{" "}
          vs AVM <span className="font-medium tabular-nums">{fmt.money(deal.avm_price)}</span>.
          A discount this deep usually means the listing has hidden
          title issues, is a foreclosure, or the AVM is stale. Pull
          comps and call the listing agent before underwriting.
        </p>
      </div>
    </div>
  );
}


// Phase 44 Batch BF2 — exported so DealDetail can also mount the
// panel BELOW the Tri-View (not only on the Financials tab). Reads
// `fmt` from useFmt internally so DealDetail doesn't need to wire it.
export function ExitAsRentalPanel({ deal, fmt: fmtProp }) {
  // Hook-safe: useFmt is the fallback when DealDetail doesn't pass `fmt`.
  const fmtHook = useFmt();
  const fmt = fmtProp || fmtHook;
  const [open, setOpen] = useState(false);
  // Best-effort rental exit — use the user's optional rental inputs
  // if they captured them on the flip wizard, else show an empty
  // state prompting them to add the data.
  const monthlyRent = Number(deal?.monthly_rent || 0);
  const taxesAnnual = Number(deal?.taxes_annual || 0);
  const insuranceAnnual = Number(deal?.insurance_annual || 0);
  const hoaMonthly = Number(deal?.hoa_monthly || 0);
  const arv = Number(deal?.arv || 0);
  const repairBudget = Number(deal?.repair_budget || 0);
  const purchasePrice = Number(deal?.purchase_price || 0);

  // Crude rental NOI estimate (full underwriting requires running
  // compute_deal — we keep this local & cheap, the user can switch
  // deal_type to "rental" for the real engine output).
  const monthlyExpenses = (taxesAnnual / 12) + (insuranceAnnual / 12) + hoaMonthly;
  const noiMonthly = monthlyRent - monthlyExpenses;
  const noiAnnual = noiMonthly * 12;
  const capRateExit = arv > 0 ? (noiAnnual / arv) : 0;
  const projectCost = purchasePrice + repairBudget;
  const capRateOnCost = projectCost > 0 ? (noiAnnual / projectCost) : 0;

  const hasRentalData = monthlyRent > 0;

  return (
    <div
      data-testid="flip-exit-as-rental"
      className="rounded-2xl border border-slate-200 bg-white shadow-sm"
    >
      <button
        type="button"
        data-testid="flip-exit-as-rental-toggle"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
      >
        <div className="flex items-center gap-2">
          {open
            ? <ChevronDown className="h-4 w-4 text-slate-500" />
            : <ChevronRight className="h-4 w-4 text-slate-500" />}
          <span className="text-sm font-semibold text-slate-900">
            Exit as Rental — what if you held it?
          </span>
        </div>
        <span className="text-xs text-slate-500">
          Compare flip vs buy-and-hold
        </span>
      </button>
      {open ? (
        <div className="border-t border-slate-100 px-5 pb-5 pt-4">
          {!hasRentalData ? (
            <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600">
              No rental income captured for this property. Add{" "}
              <span className="font-medium">monthly rent</span>,{" "}
              <span className="font-medium">taxes</span>, and{" "}
              <span className="font-medium">insurance</span> in the wizard
              to model the buy-and-hold exit.
            </div>
          ) : (
            <table className="w-full">
              <tbody>
                <Row label="Monthly rent" value={fmt.money(monthlyRent, { cents: true })} indent />
                <Row label="Monthly expenses (est.)" value={fmt.money(monthlyExpenses, { cents: true })} indent />
                <Row
                  label="NOI (monthly)"
                  value={fmt.money(noiMonthly, { cents: true })}
                  indent
                  bold
                  tone={noiMonthly >= 0 ? "good" : "bad"}
                />
                <Row label="NOI (annual)" value={fmt.money(noiAnnual)} indent />
                <Row
                  label="Cap rate on ARV"
                  value={arv > 0 ? `${(capRateExit * 100).toFixed(2)}%` : "—"}
                  indent
                />
                <Row
                  label="Cap rate on project cost"
                  value={projectCost > 0 ? `${(capRateOnCost * 100).toFixed(2)}%` : "—"}
                  indent
                />
              </tbody>
            </table>
          )}
          <p className="mt-3 text-[11px] italic text-slate-500">
            Rough estimate — change <span className="font-medium">deal type</span>{" "}
            to "Rental" for full underwriting (vacancy, management,
            financing, DSCR, cash-on-cash).
          </p>
        </div>
      ) : null}
    </div>
  );
}


export default function FlipFinancialsTab({ deal }) {
  const fmt = useFmt();
  const arv = deal.arv || 0;
  const rule70 = deal.deal_to_arv_pct;
  const rule70Pass = rule70 > 0 && rule70 <= 70;
  const profitTone = (deal.gross_profit || 0) > 0 ? "good" : "bad";

  return (
    <div className="space-y-6" data-testid="flip-financials-tab">
      {/* Phase 44 BF2 — AVM warning banner sits ABOVE the KPI strip
          so it grabs attention before the user reads the score. */}
      <AvmDiscrepancyBanner deal={deal} fmt={fmt} />

      {/* Hero KPI strip */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-orange-200 bg-orange-50 p-5 shadow-sm" data-testid="flip-kpi-arv">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-orange-700">
            <Hammer className="h-3 w-3" /> ARV
          </div>
          <div className="mt-2 text-2xl font-bold tabular-nums text-slate-900">{fmt.money(arv)}</div>
          <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
            After repair value
            <ArvSourcePill source={deal.arv_source} />
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="flip-kpi-profit">
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Gross Profit</div>
          <div className={`mt-2 text-2xl font-bold tabular-nums ${profitTone === "good" ? "text-emerald-700" : "text-red-600"}`}>
            {fmt.money(deal.gross_profit, { cents: true })}
          </div>
          <div className="mt-1 text-xs text-slate-500">Net of all costs &amp; payoff</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="flip-kpi-roi">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
            <TrendingUp className="h-3 w-3" /> Annualised ROI
          </div>
          <div className="mt-2 text-2xl font-bold tabular-nums text-slate-900">
            {deal.annualized_roi_pct != null ? `${deal.annualized_roi_pct.toFixed(1)}%` : "—"}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            Project ROI {deal.roi_pct != null ? `${deal.roi_pct.toFixed(1)}%` : "—"}
          </div>
        </div>
        <div className={`rounded-xl border p-5 shadow-sm ${rule70Pass ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`} data-testid="flip-kpi-rule70">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
            {rule70Pass ? <CheckCircle2 className="h-3 w-3 text-emerald-600" /> : <AlertCircle className="h-3 w-3 text-amber-600" />}
            70% Rule
          </div>
          <div className="mt-2 text-2xl font-bold tabular-nums text-slate-900">
            {rule70 != null ? `${rule70.toFixed(1)}%` : "—"}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            (Price + Repairs) ÷ ARV {rule70Pass ? "— passes" : "— above threshold"}
          </div>
        </div>
      </div>

      {/* Full breakdown */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">Flip P&amp;L</h3>
        <table className="mt-4 w-full">
          <tbody>
            <Row label="Project cost" bold divider />
            <Row label="Purchase price" value={fmt.money(deal.purchase_price)} indent />
            <Row label="Repair / rehab budget" value={fmt.money(deal.repair_budget)} indent />
            <Row label="Holding costs" value={fmt.money(deal.holding_costs_total)} indent />
            <Row label="Interest during hold" value={fmt.money(deal.interest_during_hold)} indent />
            <Row label="Closing costs" value={fmt.money(deal.closing_costs)} indent />
            <Row label="Total project cost" value={fmt.money(deal.total_project_cost)} indent bold />

            <Row label="Resale" bold divider />
            <Row label="ARV (after-repair value)" value={fmt.money(deal.arv)} indent />
            <Row label="Selling costs" value={`− ${fmt.money(deal.selling_costs, { cents: true })}`} indent tone="bad" />
            <Row label="Loan payoff" value={`− ${fmt.money(deal.loan_payoff, { cents: true })}`} indent tone="bad" />
            <Row label="Net sale proceeds" value={fmt.money(deal.net_sale_proceeds)} indent bold />

            <Row label="Profitability" bold divider />
            <Row label="Total cash invested" value={fmt.money(deal.total_cash_invested)} indent />
            <Row
              label="Gross profit"
              value={fmt.money(deal.gross_profit, { cents: true })}
              indent
              bold
              tone={profitTone}
            />
            <Row
              label="ROI on cash invested"
              value={deal.roi_pct != null ? `${deal.roi_pct.toFixed(2)}%` : "—"}
              indent
            />
            <Row
              label="Annualised ROI"
              value={deal.annualized_roi_pct != null ? `${deal.annualized_roi_pct.toFixed(2)}%` : "—"}
              indent
              bold
            />
            <Row label="ARV margin" value={deal.arv_margin_pct != null ? `${deal.arv_margin_pct.toFixed(2)}%` : "—"} indent />
            <Row
              label="Breakeven ARV"
              value={fmt.money(deal.breakeven_arv)}
              indent
            />
          </tbody>
        </table>
      </div>

      {/* Loan strip when financed */}
      {!deal.cash_purchase && (deal.loan_amount || 0) > 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">Financing</h3>
          <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-3">
            <div className="flex justify-between"><dt className="text-slate-500">Loan amount</dt><dd className="font-medium tabular-nums text-slate-900">{fmt.money(deal.loan_amount)}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Down payment</dt><dd className="font-medium tabular-nums text-slate-900">{fmt.money(deal.down_payment_amount)}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Holding (months)</dt><dd className="font-medium tabular-nums text-slate-900">{deal.holding_period_months ?? 0}</dd></div>
          </dl>
        </div>
      ) : null}

      {/* Phase 44 BF2 — Exit-as-Rental collapsible panel. */}
      <ExitAsRentalPanel deal={deal} fmt={fmt} />
    </div>
  );
}
